import { useMemo } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { LaundretteDetails, laundretteList } from "./laundrette.data";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://itpiapi.lancaster.ac.uk/production/api/v1/Laundrette"
    : "https://jedha.lancaster.ac.uk/production/api/v1/Laundrette";

export type CombinedLaundretteAppliances = {
  appliances: LaundretteAppliances;
  details: LaundretteDetails;
};

export type CombinedLaundretteDetails = {
  overview: LaundretteOverview;
  details: LaundretteDetails;
};

export type LaundretteOverview = {
  laundryroom: {
    location: string;
    site: string;
    available_washers: number;
    available_dryers: number;
  };
};

export interface Appliance {
  appliance_desc_key: string;
  lrm_status: string;
  appliance_type: string;
  status: string;
  out_of_service: number;
  label: string;
  avg_cycle_time: number;
  last_started: string;
  time_remaining: string;
}

export interface LaundretteAppliances {
  laundry_room_name: string;
  campus_name: string;
  appliances: Appliance[];
}

export const useLaundrettes = () => {
  return useQuery<CombinedLaundretteDetails[]>(["GetLaundrettes"], async () => {
    const response = await fetch(`${API_BASE_URL}/school/available`);
    if (!response.ok) throw new Error("Failed to get laundrette information.");
    const laundrettes: LaundretteOverview[] = await response.json();

    const detailedLaundrettes = laundrettes.map((laundrette) => ({
      overview: laundrette,
      details: laundretteList[laundrette.laundryroom.site],
    }));

    return detailedLaundrettes;
  });
};

export const useLaundrette = (location?: string) => {
  return useQuery<CombinedLaundretteAppliances>(
    ["GetLaundrette", location],
    async () => {
      const response = await fetch(
        `${API_BASE_URL}/room/appliances/${location}`
      );
      if (!response.ok)
        throw new Error("Failed to get information on this laundrette.");
      const appliances: LaundretteAppliances = await response.json();

      return {
        appliances,
        details: laundretteList[appliances.laundry_room_name],
      };
    },
    { enabled: !!location }
  );
};

export const useLaundrettesSearch = (query: string) => {
  const laundrettes = useLaundrettes();

  const searchResults = useMemo(() => {
    return query.length > 0
      ? laundrettes.data?.filter(
          (laundrette) =>
            laundrette.overview.laundryroom.site
              .toLowerCase()
              .includes(query.toLowerCase()) ||
            "laundry".includes(query.toLowerCase()) ||
            "laundrette".includes(query.toLowerCase()) ||
            "launderette".includes(query.toLowerCase())
        ) ?? []
      : [];
  }, [laundrettes, query]);

  return { ...laundrettes, data: searchResults } as UseQueryResult<
    CombinedLaundretteDetails[]
  >;
};
